import { forwardRef, useMemo } from 'react';
import {
  ComposedChart, Area, XAxis, YAxis, CartesianGrid,
  ReferenceLine, ResponsiveContainer, Tooltip,
} from 'recharts';

const fmtDuration = (s) => {
  if (s == null || s <= 0) return '-';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
};

const fmtElapsed = (s) => {
  if (s == null) return '-';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

const SEGMENT_TYPE_LABEL = {
  climb: 'Climb', descent: 'Descent', rolling: 'Rolling',
};

const buildElevationData = (chunks) => {
  const pts = [];
  let cumDist = 0;
  for (const c of chunks) {
    const d = c.distance || 0;
    cumDist += d;
    const alt = c.avg_altitude ?? null;
    if (alt != null) pts.push({ distKm: cumDist / 1000, altitude: Math.round(alt) });
  }
  return pts;
};

const BikePlanPrintContent = forwardRef(({
  chunks = [],
  bikePlanSegments = [],
  routeName = null,
}, ref) => {
  const elevData = useMemo(() => buildElevationData(chunks), [chunks]);

  const planBoundaries = useMemo(() => {
    if (!bikePlanSegments.length) return [];
    const maxEnd = Math.max(...bikePlanSegments.map(s => s.end_distance_m));
    const seen = new Set();
    const out = [];
    for (const seg of bikePlanSegments) {
      const distKm = seg.end_distance_m / 1000;
      const key = distKm.toFixed(3);
      if (seen.has(key)) continue;
      seen.add(key);
      if (seg.end_distance_m >= maxEnd) continue;
      out.push({ distKm, label: seg.end_label });
    }
    return out;
  }, [bikePlanSegments]);

  const root = {
    background: '#fff', color: '#111',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: 12, padding: '16px 20px',
  };

  const th = {
    background: '#374151', color: '#fff',
    padding: '5px 8px', textAlign: 'left',
    fontWeight: 'bold', fontSize: 11,
    border: '1px solid #d1d5db',
  };

  const td = {
    padding: '4px 8px',
    border: '1px solid #d1d5db',
    whiteSpace: 'nowrap',
  };

  const typeColor = (seg) => {
    if (seg.segment_type === 'climb')   return '#b45309';
    if (seg.segment_type === 'descent') return '#1d4ed8';
    return '#374151';
  };

  return (
    <div ref={ref} style={root}>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#111' }}>
          {routeName ? `${routeName} — Bike Plan` : 'Bike Plan'}
        </h2>
      </div>

      {elevData.length > 0 && (
        <div style={{ width: '100%', height: 220, marginBottom: 20 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={elevData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="distKm"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={v => `${v.toFixed(0)} km`}
                tick={{ fontSize: 10, fill: '#374151' }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#374151' }}
                tickFormatter={v => `${v}m`}
                width={45}
              />
              <Tooltip
                formatter={(v) => [`${v}m`, 'Altitude']}
                labelFormatter={(v) => `${Number(v).toFixed(1)} km`}
              />
              <Area
                type="monotone" dataKey="altitude"
                stroke="#4b6fa5" fill="#bfdbfe"
                strokeWidth={1.5} dot={false}
              />
              {planBoundaries.map(({ distKm, label }, idx) => (
                <ReferenceLine
                  key={`pb-${idx}`}
                  x={distKm}
                  stroke="#ea580c"
                  strokeWidth={1}
                  strokeDasharray="4 3"
                  label={{ value: label, position: 'top', fill: '#ea580c', fontSize: 9, angle: -45, offset: 4 }}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            <th style={th}>Segment</th>
            <th style={th}>Type</th>
            <th style={{ ...th, textAlign: 'right' }}>Length</th>
            <th style={{ ...th, textAlign: 'right' }}>Gain</th>
            <th style={{ ...th, textAlign: 'right' }}>Loss</th>
            <th style={{ ...th, textAlign: 'right' }}>Elapsed</th>
            <th style={{ ...th, textAlign: 'right' }}>Duration</th>
            <th style={{ ...th, textAlign: 'right' }}>Speed</th>
            <th style={{ ...th, textAlign: 'right' }}>Power</th>
            <th style={{ ...th, textAlign: 'right' }}>VAM</th>
            <th style={{ ...th, textAlign: 'right' }}>W/kg</th>
            <th style={th}>Zone</th>
          </tr>
        </thead>
        <tbody>
          {bikePlanSegments.map((seg, idx) => (
            <tr key={idx} style={{ background: idx % 2 === 0 ? '#f9fafb' : undefined }}>
              <td style={td}>{seg.start_label} → {seg.end_label}</td>
              <td style={{ ...td, color: typeColor(seg), fontWeight: 'bold' }}>
                {seg.segment_detail || SEGMENT_TYPE_LABEL[seg.segment_type] || seg.segment_type}
              </td>
              <td style={{ ...td, textAlign: 'right' }}>
                {seg.length_m ? `${(seg.length_m / 1000).toFixed(1)} km` : ''}
              </td>
              <td style={{ ...td, textAlign: 'right', color: seg.elevation_gain_m ? '#b91c1c' : undefined }}>
                {seg.elevation_gain_m ? `+${Math.round(seg.elevation_gain_m)}m` : ''}
              </td>
              <td style={{ ...td, textAlign: 'right', color: seg.elevation_loss_m ? '#0369a1' : undefined }}>
                {seg.elevation_loss_m ? `-${Math.round(seg.elevation_loss_m)}m` : ''}
              </td>
              <td style={{ ...td, textAlign: 'right' }}>{fmtElapsed(seg.elapsed_s)}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmtDuration(seg.duration_s)}</td>
              <td style={{ ...td, textAlign: 'right' }}>
                {seg.avg_speed ? `${seg.avg_speed.toFixed(1)} km/h` : '—'}
              </td>
              <td style={{ ...td, textAlign: 'right' }}>
                {seg.avg_power ? `${Math.round(seg.avg_power)} W` : '—'}
              </td>
              <td style={{ ...td, textAlign: 'right' }}>
                {seg.vam ? `${Math.round(seg.vam)} m/h` : '—'}
              </td>
              <td style={{ ...td, textAlign: 'right' }}>
                {seg.watts_per_kg ? seg.watts_per_kg.toFixed(1) : '—'}
              </td>
              <td style={td}>{seg.power_zone_label || seg.power_zone || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

BikePlanPrintContent.displayName = 'BikePlanPrintContent';
export default BikePlanPrintContent;
